<template>
  <div>
    <b-container>
      <b-alert show>
        Das Projekt Kontrastive Alphabetisierung im Situationsansatz (KASA) ist
        seit dem 31.7.2022 beendet. Diese Homepage wird nicht weiter gepflegt.
      </b-alert>
    </b-container>
    <b-container>
      <b-jumbotron lead="Kostenlose Zusatzinhalte">
        <h2 slot="header" id="content-header">Inhaltsverzeichnis</h2>

        <p>
          Hier finden Sie ein Inhaltsverzeichnis, welches alle unsere bisher erstellten zusätzlichen <b>Inhalte</b> auflistet.

          Um nach Inhalten zu suchen, nutzen Sie bitte die <router-link to="/resources/search">Suchfunktion</router-link>.
        </p>

      </b-jumbotron>
    </b-container>

    <b-container>
      <b-row>
        <b-col md="3">
          <div class="filter-buttons">
            <p class="text-muted mb-0 mt-2">Typauswahl</p>
            <resource-type-filter :enableDefaultAll="true" :always-vertical="true" ref="resourceTypeFilter"></resource-type-filter>
          </div>
          <b-nav
            v-b-scrollspy="20"
            class="section-nav"
            vertical
          >
            <p class="text-muted mb-0 mt-2">Inhaltsübersicht | <a href="#content-header">Nach oben</a></p>
            <li
              v-for="chapter in toc.toc"
              :key="getUniqueIdForEntry(chapter.title)"
              class="nav-item toc-entry toc-h2 mb-1"
            >
              <b-link
                :href="'#' + getUniqueIdForEntry(chapter.title)"
                class="nav-link"
                @click="scrollIntoView($event, getUniqueIdForEntry(chapter.title))"
              >
                <span>{{ chapter.title }} <b-badge>{{ topicSearchResultUnfiltered(chapter.title).length }}</b-badge></span>
              </b-link>
            </li>
          </b-nav>
        </b-col>
        <b-col md="9" v-if="searchResult !== undefined">
          <div v-for="topic in searchResult">
            <h3 :id="getUniqueIdForEntry(topic.topic.title)">{{topic.topic.title}}</h3>
            <p>{{topic.topic.description}}</p>
            <b-row class="mb-4" v-for="resource in topic.entries" :key="resource.filePath">
              <b-col cols="12">
                <div :id="getUniqueIdForEntry(resource.fileName)">
                  <resource :resource="resource"></resource>
                </div>
              </b-col>
            </b-row>
          </div>
          <b-row class="mb-4" v-if="searchResult.length === 0">
            <b-col cols="12" id="no-search-results" class="fadeIn animated">
              <h3>Keine Ergebnisse gefunden!</h3>
              <p>
                Es tut uns sehr leid, aber leider konnten keine Zusatzmaterialien zu ihren Parametern gefunden werden.
                Bitte stellen Sie sicher, dass Sie mindestens eine Sprache und einen Typ auswählen.
              </p>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
  import Resource from './Resource'
  import { RESOURCES_LANGUAGES } from '../constants'
  import ResourceTypeFilter from './ResourceTypeFilter'

  const Loki = require('lokijs')

  export default {
    name: 'ResourceTopicList',
    data: function () {
      const languageButtons = []
      let selected = true
      for (const lang in RESOURCES_LANGUAGES) {
        languageButtons.push({
          caption: RESOURCES_LANGUAGES[lang]['name'],
          langCode: lang,
          selected: selected
        })
        // selected = false
      }

      return {
        resourceCollection: null,
        topicsCollection: null,
        db: null,
        languageButtons: languageButtons
      }
    },
    components: {
      Resource,
      ResourceTypeFilter
    },
    mounted () {
      this.$http.get('/static/resourceDB.json').then(function (response) {
        this.db = new Loki('resources')
        this.db.loadJSONObject(response.body)
        this.resourceCollection = this.db.getCollection('resources')
        this.topicsCollection = this.db.getCollection('topics')

        this.$nextTick(() => {
          this.$forceUpdate()
        })
      })
    },
    computed: {
      filteredResources: function () {
        if (this.resourceCollection !== null) {
          return this.resourceCollection.chain().where((obj) => {
            if (this.$refs.resourceTypeFilter !== undefined) {
              return this.$refs.resourceTypeFilter.filterSelectedObjects(obj)
            }
            return false
          }).simplesort('page')
        }
      },
      searchResult: function () {
        const result = []
        if (this.resourceCollection === null) {
          return result
        }
        for (const tocIdx in this.toc['toc']) {
          const partResult = {
            topic: this.toc['toc'][tocIdx],
            entries: this.topicSearchResult(this.toc['toc'][tocIdx]['title']).data()
          }
          if (partResult['entries'].length > 0) {
            result.push(partResult)
          }
        }
        return result
      },
      toc: function () {
        if (this.topicsCollection === null) {
          return {
            toc: []
          }
        }

        return {
          toc: this.topicsCollection.chain().data()
        }
      }
    },
    methods: {
      getUniqueIdForEntry: function (entry) {
        return 'entry_' + this.hashCode(entry)
      },
      offsetTop: el => {
        // Return an element's offset wrt document element
        // https://j11y.io/jquery/#v=git&fn=jQuery.fn.offset
        if (!el.getClientRects().length) {
          return 0
        }
        const bcr = el.getBoundingClientRect()
        const win = el.ownerDocument.defaultView
        return bcr.top + win.pageYOffset
      },
      scrollIntoView (evt, href) {
        // taken from https://github.com/bootstrap-vue/bootstrap-vue/blob/dev/docs/components/toc.vue
        evt.preventDefault()
        evt.stopPropagation()
        // We use an attribute `querySelector()` rather than `getElementByID()`,
        // as some auto-generated ID's are invalid or not unique
        const id = (href || '').replace(/#/g, '')
        const $el = document.body.querySelector(`[id="${id}"]`)
        if ($el) {
          // Get the document scrolling element
          const scroller = document.scrollingElement || document.documentElement || document.body
          // Scroll heading into view (minus offset to account for nav top height
          scrollTo(scroller, this.offsetTop($el) - 70, 100, () => {
            // Set a tab index so we can focus header for a11y support
            $el.tabIndex = -1
            // Focus the heading
            $el.focus()
          })
        }
      },
      hashCode: str => {
        // taken from https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
        return str.split('').reduce((prevHash, currVal) =>
          (((prevHash << 5) - prevHash) + currVal.charCodeAt(0)) | 0, 0)
      },
      topicSearchResult: function (topic) {
        if (this.resourceCollection !== null) {
          return this.filteredResources.branch().where((obj) => {
            return obj['topic'] === topic
          })
        }
      },
      topicSearchResultUnfiltered: function (topic) {
        if (this.resourceCollection !== null) {
          return this.resourceCollection.where((obj) => {
            return obj['topic'] === topic
          })
        }
      }
    }
  }
</script>


<style lang="scss" scoped>
  @import '../style/bootstrap-component-include';
  @import '~animate.css/animate.css';

  .container {
    margin-top: 15px;
  }

  #no-search-results {
    animation-delay: 1.5s;
  }

  .section-nav {
    position: sticky;
    top: 80px;
  }

  .nav-link {
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;
  }

  .filter-buttons {
    z-index: 0;
    position: relative;
  }
</style>



// WEBPACK FOOTER //
// src/components/ResourceTopicList.vue